import { ApiError } from '../work/gateway.js';

/**
 * The browser's calls to the Stage 6 endpoints.
 *
 * SAME DISCIPLINE AS STAGE 5, and the same two things it does not do. Nothing
 * here names a tenant, because there is nothing it could name one with — the
 * server takes the tenant from the session cookie and ignores anything a
 * request says about it. And nothing here filters, counts, re-checks or hides:
 * the screens render what these calls return, and what they return has already
 * been through `accessPredicate` in SQL.
 *
 * Every unsafe call carries an `Idempotency-Key` generated HERE rather than on
 * the server, for the Stage 5 reason: a key the server minted would be a
 * different key on every retry, which is the same as having none.
 */

export interface SearchHit {
  readonly resource: string;
  readonly recordId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly moduleKey: string;
  readonly rank: number;
  readonly exact: boolean;
}

export interface SearchResults {
  readonly hits: readonly SearchHit[];
  readonly total: number;
  readonly nextCursor: {
    readonly rank: number;
    readonly resource: string;
    readonly recordId: string;
  } | null;
  readonly resources: readonly { readonly key: string; readonly label: string }[];
}

export interface SavedSearch {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly visibility: 'private' | 'tenant';
  readonly criteria: { readonly term: string };
  readonly lastRunAt: string | null;
  readonly updatedAt: string;
}

export interface ReportSummary {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly moduleKey: string;
  readonly datasetKey: string;
  readonly ownership: 'platform' | 'tenant';
  readonly visibility: 'private' | 'tenant';
  readonly updatedAt: string;
}

export interface DatasetColumn {
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly groupable: boolean;
}

export interface DatasetMeasure {
  readonly key: string;
  readonly label: string;
  readonly aggregations: readonly string[];
}

export interface DatasetSummary {
  readonly key: string;
  readonly label: string;
  readonly moduleKey: string;
  readonly columns: readonly DatasetColumn[];
  readonly measures: readonly DatasetMeasure[];
}

export interface ReportResult {
  readonly report: { readonly key: string; readonly version: number };
  readonly columns: readonly { readonly key: string; readonly label: string }[];
  readonly rows: readonly Record<string, unknown>[];
  readonly rowCount: number;
  readonly truncated: boolean;
}

export interface ExportEntry {
  readonly id: string;
  readonly source: 'report' | 'search';
  readonly format: string;
  readonly status: 'queued' | 'running' | 'ready' | 'failed' | 'stale' | 'expired';
  readonly rowCount: number | null;
  readonly failureReason: string | null;
  readonly requestedAt: string;
  readonly expiresAt: string;
}

export interface MetricCard {
  readonly key: string;
  readonly title: string;
  readonly unit: string;
  readonly basis: 'caller_scoped' | 'tenant_population';
  /** Null when the API refused this metric: see `unavailable`. Never a zero. */
  readonly total: number | null;
  readonly previousTotal: number | null;
  readonly points: readonly {
    readonly periodStart: string;
    readonly dimensionKey: string;
    readonly value: number;
  }[];
  readonly drillThroughReportKey: string | null;
  /** Set when the API refused this metric, with the reason it gave. */
  readonly unavailable?: { readonly status: number; readonly detail: string } | undefined;
}

export interface ReportDocument {
  readonly datasetKey: string;
  readonly columns: readonly string[];
  readonly filters: readonly unknown[];
  readonly groupBy: readonly string[];
  readonly aggregates: readonly { readonly measure: string; readonly aggregation: string }[];
  readonly sort: readonly { readonly key: string; readonly direction: 'asc' | 'desc' }[];
  readonly parameters: readonly unknown[];
  readonly limit: number;
}

export interface AnalyticsGateway {
  search(term: string, resources?: readonly string[]): Promise<SearchResults>;
  savedSearches(): Promise<readonly SavedSearch[]>;
  saveSearch(input: {
    readonly name: string;
    readonly visibility: 'private' | 'tenant';
    readonly term: string;
  }): Promise<void>;
  reports(): Promise<readonly ReportSummary[]>;
  datasets(): Promise<readonly DatasetSummary[]>;
  createReport(input: {
    readonly key: string;
    readonly name: string;
    readonly visibility: 'private' | 'tenant';
    readonly document: ReportDocument;
  }): Promise<void>;
  runReport(key: string): Promise<ReportResult>;
  exports(): Promise<readonly ExportEntry[]>;
  requestExport(input: {
    readonly reportKey: string;
    readonly format: 'csv' | 'xlsx';
  }): Promise<void>;
  downloadExport(exportId: string): Promise<{ readonly url: string; readonly expiresAt: string }>;
  metrics(): Promise<readonly MetricCard[]>;
}

function newKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `${String(Date.now())}-${String(Math.random()).slice(2)}`;
}

export function createAnalyticsGateway(baseUrl = '/api/v1'): AnalyticsGateway {
  async function call<T>(path: string, init?: RequestInit & { readonly key?: string }): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (init?.key !== undefined) headers['idempotency-key'] = init.key;

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) {
      // RFC 7807 from the API, read rather than guessed — so a 404 and a 403
      // reach the screen as the API worded them. The browser must not decide
      // which of the two this was: that decision is the server's, and it is
      // load-bearing.
      const problem = (await response.json().catch(() => ({}))) as {
        title?: string;
        detail?: string;
      };
      throw new ApiError(response.status, problem.title ?? 'Something went wrong', problem.detail);
    }
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }

  return {
    async search(term, resources) {
      const query = new URLSearchParams({ q: term, limit: '50' });
      if (resources !== undefined && resources.length > 0) {
        query.set('resources', resources.join(','));
      }
      return call<SearchResults>(`/search?${query.toString()}`);
    },
    async savedSearches() {
      const body = await call<{ savedSearches: readonly SavedSearch[] }>('/search/saved');
      return body.savedSearches;
    },
    async saveSearch(input) {
      await call('/search/saved', {
        method: 'POST',
        key: newKey(),
        body: JSON.stringify({
          name: input.name,
          visibility: input.visibility,
          criteria: { term: input.term },
        }),
      });
    },
    async reports() {
      const body = await call<{ reports: readonly ReportSummary[] }>('/reports');
      return body.reports;
    },
    async datasets() {
      const body = await call<{ datasets: readonly DatasetSummary[] }>('/reports/datasets');
      return body.datasets;
    },
    async createReport(input) {
      await call('/reports', { method: 'POST', key: newKey(), body: JSON.stringify(input) });
    },
    async runReport(key) {
      return call<ReportResult>(`/reports/${encodeURIComponent(key)}/run`, {
        method: 'POST',
        key: newKey(),
        body: JSON.stringify({}),
      });
    },
    async exports() {
      const body = await call<{ exports: readonly ExportEntry[] }>('/exports');
      return body.exports;
    },
    async requestExport(input) {
      await call('/exports', { method: 'POST', key: newKey(), body: JSON.stringify(input) });
    },
    async downloadExport(exportId) {
      return call<{ url: string; expiresAt: string }>(
        `/exports/${encodeURIComponent(exportId)}/download`,
        { method: 'POST', key: newKey(), body: JSON.stringify({}) },
      );
    },
    async metrics() {
      const body = await call<{ metrics: readonly MetricCard[] }>('/metrics');
      return body.metrics;
    },
  };
}
